import { useMemo } from 'react';
import { Data } from '@generated/Foundation.GMI.model';
import { useSiteSettings } from './useSiteSettings';
import { useDisabledFeatureFlags } from './useDisabledFeatureFlags';

// Sitecore checkbox field (Buy Now Settings > Pear Commerce > "Enable Legacy Pear") gating the
// legacy PDP+ static-script behavior (see Shopping.tsx).
const ENABLE_LEGACY_PEAR_SCRIPT_FIELD_NAME = 'enableLegacyPearScript';

/**
 * Hook that determines if Pear Commerce is enabled and configured, and provides the vendor ID
 * and (optional) widget ID.
 *
 * Pear Commerce is considered enabled when:
 * 1. A vendor ID is configured in the site settings
 * 2. The PEAR feature flag is not disabled
 *
 * The widget ID is an optional, per-locale identifier (e.g. Buy Now Settings for Canada English
 * vs. Canada French each have their own widget ID under the same vendor ID). When present, it
 * should be forwarded to the Pear SDK config so it can load the correct localized widget.
 *
 * @returns Object containing:
 *   - isEnabled: boolean indicating if Pear Commerce is enabled
 *   - vendorId: string | undefined - the vendor ID if configured, undefined otherwise
 *   - widgetId: string | undefined - the widget ID if configured, undefined otherwise
 *   - isLegacyPdpScriptEnabled: boolean - whether the "Enable Legacy Pear" checkbox is checked
 */
export function usePearCommerceIsEnabled(): {
  isEnabled: boolean;
  vendorId: string | undefined;
  widgetId?: string;
  isLegacyPdpScriptEnabled: boolean;
} {
  const siteSettings = useSiteSettings();
  const disabledFeatureFlags = useDisabledFeatureFlags();

  return useMemo(() => {
    const buyNowSettings = siteSettings?.buyNowSettings as Data.Settings.BuyNowSettings;
    const pearCommerceVendorID = buyNowSettings?.fields?.pearCommerceVendorId?.value;
    const pearCommerceWidgetID = buyNowSettings?.fields?.pearCommerceWidgetId?.value || undefined;
    const isLegacyPdpScriptEnabled = Boolean(
      (buyNowSettings?.fields as Record<string, { value?: boolean } | undefined> | undefined)?.[
        ENABLE_LEGACY_PEAR_SCRIPT_FIELD_NAME
      ]?.value
    );

    // Check if vendor ID is configured
    const isConfigured = Boolean(pearCommerceVendorID);

    // Check if PEAR feature flag is not disabled
    const isNotDisabled = !disabledFeatureFlags?.PEAR;

    return {
      isEnabled: isConfigured && isNotDisabled,
      vendorId: pearCommerceVendorID,
      isLegacyPdpScriptEnabled,
      ...(pearCommerceWidgetID ? { widgetId: pearCommerceWidgetID } : {}),
    };
  }, [siteSettings, disabledFeatureFlags]);
}
